// store/index.ts
import { configureStore, combineReducers, PreloadedState } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { persistReducer, persistStore, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import sessionReducer from './slices/session'
import settingsReducer from './slices/settings'
import quizReducer from './slices/quiz'
import orderReducer from './slices/order'
import backofficeOrderReducer from './slices/backofficeOrders'
import orderAmountPaidSliceReducer from './slices/orderAmountPaidSlice'
import campaignReducer from './slices/campaign'

import alertsReducer from './slices/alerts'
import { backofficeApi } from './api/backoffice'
import { customerApi } from './api/customer'
import notify from './middleware/notify'
import { publicApi } from './api/public'

const rootReducer = combineReducers({
  session: sessionReducer,
  settings: settingsReducer,
  quiz: quizReducer,
  orders: orderReducer,
  backofficeOrders: backofficeOrderReducer,
  payment: orderAmountPaidSliceReducer,
  alerts: alertsReducer,
  campaign: campaignReducer,
  [backofficeApi.reducerPath]: backofficeApi.reducer,
  [customerApi.reducerPath]: customerApi.reducer,
  [publicApi.reducerPath]: publicApi.reducer,
})

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['quiz', 'orders', 'settings'],
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

const reduxMiddleware = { serializableCheck: { ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER] } }
const middleware = [backofficeApi.middleware, customerApi.middleware, publicApi.middleware, notify]

export type RootState = ReturnType<typeof rootReducer>

export const setupStore = (preloadedState?: PreloadedState<RootState>, devTools = true) => {
  return configureStore({
    reducer: persistedReducer,
    preloadedState,
    devTools,
    middleware: getDefaultMiddleware => getDefaultMiddleware(reduxMiddleware).concat(middleware),
  })
}

export const store = setupStore()
export const persistor = persistStore(store)

setupListeners(store.dispatch)

export type AppStore = ReturnType<typeof setupStore>
export type AppDispatch = typeof store.dispatch
